import {
    type Command,
    type CommandHandler,
    type CommandService,
    CommandRegistry,
    type Disposable,
} from '@patsnap/tahiti-core';

declare const commandArgs: unique symbol;
declare const commandResult: unique symbol;

/**
 * 将 Tahiti Command 与参数、返回值关联的编译期契约。
 * 运行时仍使用 Tahiti CommandRegistry，不增加第二套命令运行时。
 */
export interface TypedCommand<Args extends unknown[], Result> extends Command {
    readonly [commandArgs]?: Args;
    readonly [commandResult]?: Result;
}

export type TypedCommandArgs<C> = C extends TypedCommand<infer Args, unknown>
    ? Args
    : never;

export type TypedCommandResult<C> = C extends TypedCommand<unknown[], infer Result>
    ? Result
    : never;

/** 类型安全的命令处理器。 */
export interface TypedCommandHandler<C extends TypedCommand<unknown[], unknown>> {
    /**
     * 执行命令。
     * @param args 命令定义声明的参数
     * @returns 命令定义声明的结果或其 Promise
     */
    execute(
        ...args: TypedCommandArgs<C>
    ): TypedCommandResult<C> | Promise<TypedCommandResult<C>>;
    isEnabled?(...args: TypedCommandArgs<C>): boolean;
    isVisible?(...args: TypedCommandArgs<C>): boolean;
    isToggled?(...args: TypedCommandArgs<C>): boolean;
}

/** 仅暴露类型安全执行能力的稳定抽象。 */
export interface TypedCommandExecutor {
    /**
     * 执行命令并等待处理器完成。
     * @param command 类型化命令定义
     * @param args 命令参数
     * @returns 命令处理结果
     * @throws 没有活动处理器时抛出 `NO_ACTIVE_HANDLER`，处理器异常原样传播
     */
    executeCommand<C extends TypedCommand<unknown[], unknown>>(
        command: C,
        ...args: TypedCommandArgs<C>
    ): Promise<TypedCommandResult<C>>;
}

/** 仅暴露类型安全注册能力的稳定抽象。 */
export interface TypedCommandRegistrar {
    /**
     * 注册命令的唯一 owner。
     * @returns 注销 owner 的 Disposable
     */
    registerCommand<C extends TypedCommand<unknown[], unknown>>(
        command: C,
        handler: TypedCommandHandler<C>,
    ): Disposable;
}

/**
 * 定义类型化命令。
 * @param command Tahiti 命令元数据
 * @returns 带参数和结果类型的命令定义
 */
export function defineCommand<Args extends unknown[], Result>(
    command: Command,
): TypedCommand<Args, Result> {
    return command;
}

/** Tahiti CommandRegistry 的类型安全适配器。 */
export class TypedCommandRegistry implements TypedCommandExecutor, TypedCommandRegistrar {
    constructor(private readonly registry: CommandRegistry) {}

    /**
     * 注册类型安全的命令处理器。
     * @param command 类型化命令定义
     * @param handler 类型化处理器
     * @returns 注销命令及处理器的 Disposable
     */
    registerCommand<C extends TypedCommand<unknown[], unknown>>(
        command: C,
        handler: TypedCommandHandler<C>,
    ): Disposable {
        if (this.registry.getCommand(command.id)) {
            throw new Error(`命令已注册，单一 owner 约束被破坏：${command.id}`);
        }
        const tahitiHandler: CommandHandler = {
            execute: (...args: unknown[]) => handler.execute(...args as TypedCommandArgs<C>),
            isEnabled: handler.isEnabled
                ? (...args: unknown[]) => handler.isEnabled!(...args as TypedCommandArgs<C>)
                : undefined,
            isVisible: handler.isVisible
                ? (...args: unknown[]) => handler.isVisible!(...args as TypedCommandArgs<C>)
                : undefined,
            isToggled: handler.isToggled
                ? (...args: unknown[]) => handler.isToggled!(...args as TypedCommandArgs<C>)
                : undefined,
        };
        return this.registry.registerCommand(command, tahitiHandler);
    }

    executeCommand<C extends TypedCommand<unknown[], unknown>>(
        command: C,
        ...args: TypedCommandArgs<C>
    ): Promise<TypedCommandResult<C>> {
        const service: CommandService = this.registry;
        return service.executeCommand<TypedCommandResult<C>>(
            command.id,
            ...args,
        ) as Promise<TypedCommandResult<C>>;
    }
}
